<template>
  <div class="absolute ml-[25vw] mt-[12.5vh] h-[75vh] w-[50vw] transform-gpu overflow-hidden opacity-20 blur-3xl">
    <AppBackgroundAnimated />
  </div>
  <div class="m-0 h-screen w-screen p-0">
    <div
      class="appear z-20 flex min-h-full flex-1 -translate-y-4 flex-col justify-center p-0 px-6 py-12 opacity-0 transition-all duration-700 lg:px-8"
      :class="{ '!translate-y-0 opacity-100': loaded }">
      <AppSignLogo />
      <div class="p-[1px] sm:mx-auto sm:w-full sm:max-w-sm">
        <h1 class="text-center text-xl font-normal leading-9 tracking-tight text-white">
          {{ $t('sign.up.index.title') }}
        </h1>

        <template v-if="configStore.oidc.length && !route.query.email">
          <AppOIDCAuth :region="targetRegion" class="mt-6" :prefix="$t('sign.up.index.signUpWith')" />
          <AppDivider
            class="my-5"
            color="border-gray-300"
            :label="$t('sign.up.index.or')"
            position="center"
            background="bg-gray-700"
            ><span class="pl-2 text-gray-300">{{ $t('sign.up.index.or') }}</span></AppDivider
          >
        </template>

        <form class="mt-4 space-y-6" action="#" method="POST">
          <p class="text-sm text-gray-300">{{ $t('sign.up.index.requiredHint') }}</p>
          <div>
            <label id="region-label" class="block text-sm font-medium leading-6 text-white">{{
              $t('sign.up.index.region')
            }}</label>
            <div class="mt-2">
              <FormFieldSelect
                id="region"
                name="region"
                ariaLabelledby="region-label"
                :validation-rules="[]"
                :options="regions"
                :selected="selectedRegion"
                selected-clazz="text-gray-300 font-semibold"
                @change="changeRegion" />
            </div>
          </div>

          <div>
            <label for="name" class="block text-sm font-medium leading-6 text-white"
              >{{ $t('sign.up.index.name') }} *</label
            >
            <div class="mt-2">
              <FormFieldTextInput
                id="name"
                :ariaLabel="$t('sign.up.index.name')"
                v-model="form.name"
                name="name"
                type="text"
                autocomplete="name"
                required
                focus />
            </div>
          </div>

          <div>
            <label for="company" class="block text-sm font-medium leading-6 text-white">{{
              $t('sign.up.index.company')
            }}</label>
            <div class="mt-2">
              <FormFieldTextInput
                id="company"
                :ariaLabel="$t('sign.up.index.company')"
                v-model="form.company"
                name="company"
                type="text"
                autocomplete="organization" />
            </div>
          </div>

          <div>
            <label for="email" class="block text-sm font-medium leading-6 text-white"
              >{{ $t('sign.up.index.email') }} *</label
            >
            <div class="mt-2">
              <FormFieldTextInput
                id="email"
                :ariaLabel="$t('sign.up.index.email')"
                v-model="form.email"
                name="email"
                type="email"
                required />
            </div>
          </div>

          <transition name="slide">
            <div v-if="form.email.length > 0" class="p-[1px]">
              <label for="email2" class="block text-sm font-medium leading-6 text-white"
                >{{ $t('sign.up.index.emailRetype') }} *</label
              >
              <div class="mt-2">
                <FormFieldTextInput
                  id="email2"
                  :ariaLabel="$t('sign.up.index.emailRetype')"
                  v-model="form.email2"
                  name="email2"
                  type="email"
                  required
                  :ariaInvalid="invalidField === 'email2'"
                  :ariaDescribedby="invalidField === 'email2' ? 'sign-up-error' : undefined" />
              </div>
            </div>
          </transition>

          <div>
            <div class="flex items-center justify-between">
              <label for="password" class="block text-sm font-medium leading-6 text-white"
                >{{ $t('sign.up.index.password') }} *</label
              >
            </div>
            <div class="mt-2 flex flex-row space-x-2">
              <FormFieldTextInput
                id="password"
                v-model="form.password"
                name="password"
                class="w-full"
                :ariaLabel="$t('sign.up.index.password')"
                :type="revealPassword ? 'text' : 'password'"
                autocomplete="new-password"
                ariaDescribedby="password-hint"
                required
                placeholder=""
                @focus="passwordFocused = true"
                @blur="passwordFocused = false">
                <template #suffix>
                  <ficon
                    v-if="form.password.length > 0"
                    :icon="`fa-solid ${revealPassword ? 'fa-eye' : 'fa-eye-slash'}`"
                    class="mt-[2px] cursor-pointer text-xs text-gray-500 hover:text-gray-300"
                    :class="{ '!text-gray-300': revealPassword }"
                    @click="revealPassword = !revealPassword" />
                </template>
              </FormFieldTextInput>
              <Transition
                enter-from-class="opacity-0 translate-x-4"
                enter-to-class="opacity-100 translate-x-0"
                leave-from-class="opacity-100 translate-x-0"
                leave-to-class="opacity-0 translate-x-4">
                <AppPasswordComplexityChecker v-if="passwordFocused" :password="form.password" />
              </Transition>
            </div>
            <p id="password-hint" class="mt-2 text-xs text-gray-300">{{ $t('sign.up.index.passwordHint') }}</p>
          </div>

          <transition name="slide">
            <div v-if="form.password.length > 0" class="p-[1px]">
              <div>
                <label for="password2" class="block text-sm font-medium leading-6 text-white"
                  >{{ $t('sign.up.index.passwordAgain') }} *</label
                >
              </div>
              <div class="mt-2">
                <FormFieldTextInput
                  id="password2"
                  v-model="form.password2"
                  name="password2"
                  :type="revealPassword ? 'text' : 'password'"
                  :ariaLabel="$t('sign.up.index.passwordAgain')"
                  autocomplete="new-password"
                  required
                  :ariaInvalid="invalidField === 'password2'"
                  :ariaDescribedby="invalidField === 'password2' ? 'sign-up-error' : undefined"
                  placeholder="">
                  <template #suffix>
                    <ficon
                      v-if="form.password2.length > 0"
                      :icon="`fa-solid ${revealPassword ? 'fa-eye' : 'fa-eye-slash'}`"
                      class="mt-[2px] cursor-pointer text-xs text-gray-500 hover:text-gray-300"
                      :class="{ '!text-gray-300': revealPassword }"
                      @click="revealPassword = !revealPassword" />
                  </template>
                </FormFieldTextInput>
              </div>
            </div>
          </transition>

          <div class="text-center">
            <VueRecaptcha
              class="ml-10"
              :sitekey="configStore.reCAPTCHA"
              @verify="
                (token: string) => {
                  form.captcha = token
                }
              " />
          </div>

          <div class="flex flex-col items-center justify-start">
            <span id="sign-up-error" ref="errorEl" role="alert" class="text-red-500" />
          </div>

          <div>
            <button
              type="submit"
              class="flex w-full justify-center rounded-md bg-primary-500 px-3 py-1.5 cursor-pointer text-sm font-semibold leading-6 text-white shadow-sm hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
              @click.prevent="signUp">
              {{ $t('sign.up.index.submit') }}
            </button>
          </div>
        </form>

        <p class="mt-6 text-center text-xs text-gray-300">
          {{ $t('sign.up.index.tosPre') }}
          <NuxtLink target="_blank" href="https://helmut.cloud/tos" class="underline">
            {{ $t('sign.up.index.tos') }}
          </NuxtLink>
          {{ $t('sign.up.index.and') }}
          <NuxtLink target="_blank" href="https://helmut.cloud/privacy" class="underline">
            {{ $t('sign.up.index.privacyPolicy') }} </NuxtLink
          >.
        </p>

        <p class="mt-6 text-center text-sm text-gray-400">
          {{ $t('sign.up.index.alreadyMember') }}
          <NuxtLink :to="{ name: 'sign-in' }" class="font-semibold leading-6 text-primary-400 hover:text-primary-300">
            {{ $t('sign.up.index.signIn') }}
          </NuxtLink>
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Region } from '@moovit-sp-gmbh/hcloud-sdk/lib/interfaces/frontend/config'
import HCloudError from '@moovit-sp-gmbh/hcloud-sdk/lib/interfaces/global/HCloudError'
import { VueRecaptcha } from 'vue-recaptcha'

const { t } = useI18n()

useMenu({ show: false })
useBreadcrumb({ show: false })
definePageMeta({
  layout: 'sign'
})
useHead({ title: () => t('sign.up.index.pageTitle') })

const passwordFocused = ref(false)
const revealPassword = ref(false)

const loaded = ref(false)
onMounted(() => {
  setTimeout(() => {
    loaded.value = true
  }, 10)
})

const errorEl = ref()

const { configStore } = await useStore('configStore')

const route = useRoute()

const form = reactive({
  name: route.query.name?.toString() ?? '',
  email: route.query.email?.toString() ?? '',
  email2: route.query.email?.toString() ?? '',
  verificationCode: route.query.verificationCode?.toString() ?? undefined,
  company: '',
  password: '',
  password2: '',
  captcha: ''
})

type RegionDTO = {
  label: string
  value: string
  icon?: string
  requireAltKey?: boolean
  enabled?: boolean
}

const regions = computed(() => {
  return configStore.regions
    .filter((e: Region) => e.enabled)
    .map(
      region =>
        ({
          label: region.name,
          value: region.id,
          icon: region.icon,
          requireAltKey: region.dev
        }) as RegionDTO
    )
})

const selectedRegion = computed(() => {
  return route.query.region
    ? (regions.value.find((r: RegionDTO) => {
        return r.value === route.query.region
      }) ?? regions.value[0])
    : regions.value[0]
})

// set first region from region array unless ?region=<region-id> is set
// query-string is set after being forwarded from the sign-in page if an unknown mail has been used
const targetRegion = ref<Region>(
  configStore.regions.find(r => r.id === selectedRegion.value?.value) ?? configStore.regions[0]!
)

const changeRegion = (region: RegionDTO) => {
  targetRegion.value = configStore.regions.find(r => r.id === region.value) ?? targetRegion.value
}

const invalidField = ref<'email2' | 'password2'>()

const setInlineError = (value: string, field?: 'email2' | 'password2') => {
  errorEl.value.innerText = value
  invalidField.value = field
  if (field) document.getElementById(field)?.focus()
}

const signUp = () => {
  setInlineError('')
  useHcloud().setServer(targetRegion.value.server)

  if (form.email !== form.email2) {
    setInlineError(t('sign.up.index.errors.emailMismatch'), 'email2')
    return
  }

  if (form.password !== form.password2) {
    setInlineError(t('sign.up.index.errors.passwordMismatch'), 'password2')
    return
  }

  if (form.captcha === '') {
    setInlineError(t('sign.up.index.errors.invalidCaptcha'))
    return
  }

  useHcloud()
    .Idp.registration.register(
      form.name,
      form.email,
      form.password,
      form.captcha,
      form.company.length ? form.company : undefined,
      undefined,
      `${self.location.protocol}//${self.location.host}`,
      form.verificationCode
    )
    .then(() => {
      if (form.verificationCode) {
        navigateTo({
          name: 'sign-in',
          query: { region: targetRegion.value.id, email: form.email, redirect: '/account/user/invitations' }
        })
      } else {
        navigateTo({ name: 'sign-up-thanks', query: { email: form.email } })
      }
    })
    .catch((e: HCloudError) => {
      useDebug(Namespace.IDP).extend('sign')('failed to register', e.message)
      setInlineError(t('sign.up.index.errors.registerFailedPrefix') + useTranslateError(e))
    })
}
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.5s ease;
  /* clip only while sliding, otherwise the focus rings of the fields inside are cut off */
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  height: 0;
}
</style>
